import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { X, Loader2, MapPin, Search, Calendar, GitBranch, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import MultiImageUpload from "@/components/MultiImageUpload";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const SpeciesSubmissionModal = ({ onClose, onSubmit, prefillData }) => {
  const [loading, setLoading] = useState(false);
  const [diveSites, setDiveSites] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locationData, setLocationData] = useState(null);
  const [taxonomyData, setTaxonomyData] = useState(null);
  const [existingSpecies, setExistingSpecies] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [useFullTaxonomy, setUseFullTaxonomy] = useState(false);
  // Custom-input toggles per taxonomic level. Persisted in submission as
  // `custom_taxonomy_flags` so the review panel can preserve the choice.
  const [customTaxonomy, setCustomTaxonomy] = useState({
    phylum: false,
    class_name: false,
    order: false,
    family: false,
    genus: false,
  });
  // Optional sub-taxa toggles (subphylum, subclass, suborder, subfamily, subgenus).
  const [showSubTaxa, setShowSubTaxa] = useState({
    phylum: false,
    class_name: false,
    order: false,
    family: false,
    genus: false,
  });
  // Multiple common names (a.k.a. "also known as"). The first item is the primary name.
  const [commonNames, setCommonNames] = useState(
    prefillData?.species_name ? [prefillData.species_name] : []
  );
  const [newCommonName, setNewCommonName] = useState("");
  // When user picks from the "did you see one of these?" helper, store the id
  // so the admin's review panel can default to "merge with existing".
  const [mergeWithSpeciesId, setMergeWithSpeciesId] = useState(null);

  const [formData, setFormData] = useState({
    species_name: prefillData?.species_name || "",
    suggested_category: prefillData?.suggested_category || "",
    dive_site_id: prefillData?.dive_site_id || "",
    dive_site_name: prefillData?.dive_site_name || "",
    country: prefillData?.country || "",
    region: prefillData?.region || "",
    location_name: prefillData?.location_name || "",
    location_id: prefillData?.location_id || "",
    dive_report_id: prefillData?.dive_report_id || null,
    image_urls: [],
    observation_date: new Date().toISOString().split('T')[0],
    use_full_taxonomy: false,
    taxonomy: {
      kingdom: "Animalia",
      phylum: "",
      subphylum: "",
      class_name: "",
      subclass: "",
      order: "",
      suborder: "",
      family: "",
      subfamily: "",
      genus: "",
      subgenus: "",
      species: ""
    }
  });

  // Auto-derived scientific name from taxonomy (Genus + species).
  const derivedScientificName = useMemo(() => {
    const g = (formData.taxonomy.genus || "").trim();
    const s = (formData.taxonomy.species || "").trim();
    return g && s ? `${g} ${s}` : "";
  }, [formData.taxonomy.genus, formData.taxonomy.species]);

  // Add / remove common names
  const addCommonName = () => {
    const v = newCommonName.trim();
    if (!v) return;
    if (commonNames.includes(v)) {
      toast.error("Already added");
      return;
    }
    const next = [...commonNames, v];
    setCommonNames(next);
    setNewCommonName("");
    setFormData(prev => ({ ...prev, species_name: next[0] }));
    setSearchQuery(next[0]);
  };
  const removeCommonName = (idx) => {
    const next = commonNames.filter((_, i) => i !== idx);
    setCommonNames(next);
    setFormData(prev => ({ ...prev, species_name: next[0] || "" }));
    setSearchQuery(next[0] || "");
  };

  // Build reverse lookup map for taxonomy (family -> order -> class -> phylum)
  const taxonomyReverseLookup = useMemo(() => {
    if (!taxonomyData?.tree) return {};
    
    const lookup = {
      families: {},  // family -> { order, class_name, phylum }
      orders: {},    // order -> { class_name, phylum }
      classes: {},   // class_name -> { phylum }
    };
    
    Object.entries(taxonomyData.tree).forEach(([phylum, phylumData]) => {
      if (phylumData.classes) {
        Object.entries(phylumData.classes).forEach(([className, classData]) => {
          lookup.classes[className] = { phylum };
          
          if (classData.orders) {
            Object.entries(classData.orders).forEach(([order, orderData]) => {
              lookup.orders[order] = { class_name: className, phylum };
              
              if (orderData.families) {
                Object.keys(orderData.families).forEach(family => {
                  lookup.families[family] = { order, class_name: className, phylum };
                });
              }
            });
          }
        });
      }
    });
    
    return lookup;
  }, [taxonomyData]);

  useEffect(() => {
    fetchDiveSites();
    fetchCategories();
    fetchLocationData();
    fetchTaxonomyData();
    fetchAllLocations();
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      searchExistingSpecies(searchQuery);
    } else {
      setExistingSpecies([]);
    }
  }, [searchQuery]);

  const fetchDiveSites = async () => {
    try {
      // Use the new endpoint that includes country info
      const response = await axios.get(`${API}/map/dive-sites/with-country`);
      setDiveSites(response.data);
    } catch (error) {
      console.error("Error loading dive sites:", error);
      // Fallback to regular endpoint
      try {
        const fallback = await axios.get(`${API}/map/dive-sites`);
        setDiveSites(fallback.data);
      } catch (e) {
        console.error("Fallback also failed:", e);
      }
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/marine-database/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const fetchLocationData = async () => {
    try {
      const response = await axios.get(`${API}/marine-database/locations`);
      setLocationData(response.data);
    } catch (error) {
      console.error("Error loading locations:", error);
    }
  };

  const fetchAllLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setAllLocations(response.data);
    } catch (error) {
      console.error("Error loading all locations:", error);
    }
  };

  const fetchTaxonomyData = async () => {
    try {
      const response = await axios.get(`${API}/marine-database/taxonomy-tree`);
      setTaxonomyData(response.data);
    } catch (error) {
      console.error("Error loading taxonomy:", error);
    }
  };

  const searchExistingSpecies = async (query) => {
    try {
      const response = await axios.get(`${API}/marine-database/search?q=${encodeURIComponent(query)}&limit=5`);
      setExistingSpecies(response.data);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error searching species:", error);
    }
  };

  const handleDiveSiteSelect = (siteId) => {
    if (siteId === "N/A") {
      setFormData({
        ...formData,
        dive_site_id: "",
        dive_site_name: "N/A",
      });
      return;
    }
    const site = diveSites.find(s => s.id === siteId);
    if (!site) return;

    // Reverse cascade: a dive site uniquely determines its parent location,
    // country and region, so populate them all from the chosen site. This
    // mirrors how the taxonomy cascade works.
    const parentLoc = site.location_id
      ? allLocations.find(l => l.id === site.location_id)
      : null;
    const country = site.country || parentLoc?.country || formData.country || "";

    // Try to infer region from locationData if not already set
    let region = formData.region || "";
    if (!region && country && locationData?.all_regions) {
      for (const [rName, rData] of Object.entries(locationData.all_regions)) {
        const countries = rData?.countries || [];
        if (countries.some(c => (c || "").toLowerCase() === country.toLowerCase())) {
          region = rName;
          break;
        }
      }
    }

    setFormData({
      ...formData,
      dive_site_id: site.id,
      dive_site_name: site.name,
      country,
      region,
      location_id: parentLoc?.id || site.location_id || formData.location_id || "",
      location_name: parentLoc?.name || site.location || formData.location_name || "",
    });
  };

  const handleRegionSelect = (region) => {
    setFormData({
      ...formData,
      region: region,
      country: "",
      location_name: "",
      location_id: "",
      dive_site_id: "",
      dive_site_name: ""
    });
  };

  const handleCountrySelect = (country) => {
    setFormData({
      ...formData,
      country: country,
      location_name: "",
      location_id: "",
      dive_site_id: "",
      dive_site_name: ""
    });
  };

  const handleAreaSelect = (locationId) => {
    if (locationId === "SKIP") {
      setFormData({
        ...formData,
        location_name: "",
        location_id: "",
        dive_site_id: "",
        dive_site_name: ""
      });
      return;
    }
    const loc = allLocations.find(l => l.id === locationId);
    if (loc) {
      setFormData({
        ...formData,
        location_name: loc.name,
        location_id: loc.id,
        dive_site_id: "",
        dive_site_name: ""
      });
    }
  };

  // Get available countries for selected region
  const getCountriesForRegion = () => {
    if (!formData.region || !locationData?.all_regions) return [];
    return locationData.all_regions[formData.region]?.countries || [];
  };

  // Get areas (locations) for selected country
  const getAreasForCountry = () => {
    if (!formData.country) return [];
    const countryLower = formData.country.toLowerCase();
    return allLocations.filter(loc => 
      loc.country && loc.country.toLowerCase() === countryLower
    );
  };

  // Get dive sites for selected area/country - improved matching
  const getDiveSitesForCountry = () => {
    // If an area is selected, filter by location_id
    if (formData.location_id) {
      return diveSites.filter(site => site.location_id === formData.location_id);
    }
    if (!formData.country) return diveSites;
    
    const countryLower = formData.country.toLowerCase();
    return diveSites.filter(site => {
      // Check site's country field
      if (site.country && site.country.toLowerCase().includes(countryLower)) {
        return true;
      }
      // Check site's location field
      if (site.location && site.location.toLowerCase().includes(countryLower)) {
        return true;
      }
      return false;
    });
  };

  // Auto-fill taxonomy from family (reverse lookup)
  const handleFamilySelect = (family) => {
    const parentInfo = taxonomyReverseLookup.families[family];
    if (parentInfo) {
      setFormData({
        ...formData,
        taxonomy: {
          ...formData.taxonomy,
          family,
          order: parentInfo.order,
          class_name: parentInfo.class_name,
          phylum: parentInfo.phylum
        }
      });
    } else {
      setFormData({
        ...formData,
        taxonomy: { ...formData.taxonomy, family }
      });
    }
  };

  // Auto-fill taxonomy from order (reverse lookup)
  const handleOrderSelect = (order) => {
    const parentInfo = taxonomyReverseLookup.orders[order];
    if (parentInfo) {
      setFormData({
        ...formData,
        taxonomy: {
          ...formData.taxonomy,
          order,
          class_name: parentInfo.class_name,
          phylum: parentInfo.phylum,
          family: "" // Clear family when changing order
        }
      });
    } else {
      setFormData({
        ...formData,
        taxonomy: { ...formData.taxonomy, order, family: "" }
      });
    }
  };

  // Auto-fill taxonomy from class (reverse lookup)
  const handleClassSelect = (className) => {
    const parentInfo = taxonomyReverseLookup.classes[className];
    if (parentInfo) {
      setFormData({
        ...formData,
        taxonomy: {
          ...formData.taxonomy,
          class_name: className,
          phylum: parentInfo.phylum,
          order: "", // Clear lower levels
          family: ""
        }
      });
    } else {
      setFormData({
        ...formData,
        taxonomy: { ...formData.taxonomy, class_name: className, order: "", family: "" }
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const primary = (commonNames[0] || formData.species_name || "").trim();
    if (!primary) {
      toast.error("Please enter at least one common name");
      return;
    }

    if (!formData.region) {
      toast.error("Please select a region");
      return;
    }

    if (!formData.country) {
      toast.error("Please select a country");
      return;
    }

    if (!formData.image_urls || formData.image_urls.length === 0) {
      toast.error("At least one photo is required");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");

      // Only include taxonomy if using full taxonomy mode AND at least one field is filled
      const hasTaxonomy = useFullTaxonomy && (
        formData.taxonomy.phylum ||
        formData.taxonomy.class_name ||
        formData.taxonomy.order ||
        formData.taxonomy.family ||
        formData.taxonomy.genus
      );

      const submitData = {
        ...formData,
        species_name: primary,
        common_names: commonNames.length > 0 ? commonNames : [primary],
        scientific_name: derivedScientificName || null,
        use_full_taxonomy: useFullTaxonomy,
        taxonomy: hasTaxonomy ? formData.taxonomy : null,
        custom_taxonomy_flags: hasTaxonomy ? customTaxonomy : null,
        merge_with_species_id: mergeWithSpeciesId || null,
      };

      await axios.post(`${API}/marine-database/submissions`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onSubmit();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit species");
    } finally {
      setLoading(false);
    }
  };

  const selectExistingSpecies = (species) => {
    const primary = species.common_names?.[0] || "";
    setFormData(prev => ({
      ...prev,
      species_name: primary,
      // If the helper is offering an existing entry, pre-fill taxonomy/category
      // hints so admins see exactly which one was matched.
      suggested_category: species.simple_category || prev.suggested_category,
    }));
    setSearchQuery(primary);
    // Replace common names with the picked species' common_names so the user
    // can still add additional alternative names if they want.
    setCommonNames(species.common_names || (primary ? [primary] : []));
    // Track this for the review panel — admin will default to merge with this species.
    setMergeWithSpeciesId(species.id);
    setShowSuggestions(false);
  };

  // Get unique phyla from taxonomy tree
  const getPhyla = () => {
    if (!taxonomyData?.tree) return [];
    return Object.keys(taxonomyData.tree);
  };

  // Get classes for selected phylum
  const getClasses = () => {
    if (!taxonomyData?.tree || !formData.taxonomy.phylum) return [];
    const phylumData = taxonomyData.tree[formData.taxonomy.phylum];
    return phylumData?.classes ? Object.keys(phylumData.classes).sort() : [];
  };

  // Get orders for selected class (filtered by current selection)
  const getOrders = () => {
    if (!taxonomyData?.tree) return [];
    
    // If we have a specific class selected, get orders from that class
    if (formData.taxonomy.phylum && formData.taxonomy.class_name) {
      const classData = taxonomyData.tree[formData.taxonomy.phylum]?.classes?.[formData.taxonomy.class_name];
      return classData?.orders ? Object.keys(classData.orders).sort() : [];
    }
    
    // If only class is selected (via reverse lookup), filter orders that belong to that class
    if (formData.taxonomy.class_name) {
      const filteredOrders = Object.entries(taxonomyReverseLookup.orders || {})
        .filter(([_, info]) => info.class_name === formData.taxonomy.class_name)
        .map(([order]) => order);
      return filteredOrders.sort();
    }
    
    return [];
  };

  // Get families for selected order (filtered by current selection)
  const getFamilies = () => {
    if (!taxonomyData?.tree) return [];
    
    // If we have full path, get families from that order
    if (formData.taxonomy.phylum && formData.taxonomy.class_name && formData.taxonomy.order) {
      const orderData = taxonomyData.tree[formData.taxonomy.phylum]?.classes?.[formData.taxonomy.class_name]?.orders?.[formData.taxonomy.order];
      return orderData?.families ? Object.keys(orderData.families).sort() : [];
    }
    
    // If order is selected, filter families that belong to that order
    if (formData.taxonomy.order) {
      const filteredFamilies = Object.entries(taxonomyReverseLookup.families || {})
        .filter(([_, info]) => info.order === formData.taxonomy.order)
        .map(([family]) => family);
      return filteredFamilies.sort();
    }
    
    // If only class is selected, filter families that belong to that class
    if (formData.taxonomy.class_name) {
      const filteredFamilies = Object.entries(taxonomyReverseLookup.families || {})
        .filter(([_, info]) => info.class_name === formData.taxonomy.class_name)
        .map(([family]) => family);
      return filteredFamilies.sort();
    }
    
    return [];
  };

  // Get filtered families based on current selection (for smart filtering)
  const getFilteredFamilies = () => {
    // If order is selected, show only families in that order
    if (formData.taxonomy.order) {
      return getFamilies();
    }
    // If class is selected, show only families in that class
    if (formData.taxonomy.class_name) {
      const filteredFamilies = Object.entries(taxonomyReverseLookup.families || {})
        .filter(([_, info]) => info.class_name === formData.taxonomy.class_name)
        .map(([family]) => family);
      return filteredFamilies.sort();
    }
    // If phylum is selected, show only families in that phylum
    if (formData.taxonomy.phylum) {
      const filteredFamilies = Object.entries(taxonomyReverseLookup.families || {})
        .filter(([_, info]) => info.phylum === formData.taxonomy.phylum)
        .map(([family]) => family);
      return filteredFamilies.sort();
    }
    // No filter - show all
    return Object.keys(taxonomyReverseLookup.families || {}).sort();
  };

  // Get filtered orders based on current selection
  const getFilteredOrders = () => {
    // If class is selected, show only orders in that class
    if (formData.taxonomy.class_name) {
      const filteredOrders = Object.entries(taxonomyReverseLookup.orders || {})
        .filter(([_, info]) => info.class_name === formData.taxonomy.class_name)
        .map(([order]) => order);
      return filteredOrders.sort();
    }
    // If phylum is selected, show only orders in that phylum
    if (formData.taxonomy.phylum) {
      const filteredOrders = Object.entries(taxonomyReverseLookup.orders || {})
        .filter(([_, info]) => info.phylum === formData.taxonomy.phylum)
        .map(([order]) => order);
      return filteredOrders.sort();
    }
    // No filter - show all
    return Object.keys(taxonomyReverseLookup.orders || {}).sort();
  };

  // Get filtered classes based on current selection
  const getFilteredClasses = () => {
    // If phylum is selected, show only classes in that phylum
    if (formData.taxonomy.phylum) {
      return getClasses();
    }
    // No filter - show all
    return Object.keys(taxonomyReverseLookup.classes || {}).sort();
  };

  const filteredDiveSites = getDiveSitesForCountry();

  return (
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div 
        className="bg-white sm:rounded-2xl w-full sm:max-w-lg h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-hidden rounded-t-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Submit Species Sighting</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1 overscroll-contain">
          {/* Species Common Names with Search */}
          <div className="relative">
            <Label htmlFor="species_name">Common Name *</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="species_name"
                value={searchQuery || formData.species_name}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setFormData({ ...formData, species_name: e.target.value });
                  // Typing again invalidates the previous merge-with selection
                  if (mergeWithSpeciesId) setMergeWithSpeciesId(null);
                }}
                onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                placeholder="e.g., Whale Shark, Manta Ray"
                className="pl-9"
                autoCapitalize="words"
                autoComplete="off"
                spellCheck={false}
                data-testid="species-common-name-input"
              />
            </div>

            {/* Existing species suggestions — now with thumbnail like dive report */}
            {showSuggestions && existingSpecies.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-72 overflow-y-auto">
                <div className="p-2 text-xs text-gray-500 border-b">Did you see one of these?</div>
                {existingSpecies.map((species) => {
                  const thumb = (species.images && species.images[0]) || null;
                  return (
                    <button
                      key={species.id}
                      type="button"
                      onClick={() => selectExistingSpecies(species)}
                      className="w-full px-3 py-2 text-left hover:bg-cyan-50 flex items-center gap-3"
                      data-testid={`species-suggestion-${species.id}`}
                    >
                      {thumb ? (
                        <img
                          src={thumb.startsWith("http") ? thumb : `${BACKEND_URL}${thumb}`}
                          alt={species.common_names?.[0] || "species"}
                          className="w-10 h-10 rounded-md object-cover bg-gray-100 flex-shrink-0"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-cyan-50 flex items-center justify-center flex-shrink-0">
                          <Search className="w-4 h-4 text-cyan-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{species.common_names?.[0]}</div>
                        <div className="text-xs text-gray-500 italic truncate">{species.scientific_name}</div>
                      </div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => {
                    setShowSuggestions(false);
                    setUseFullTaxonomy(true);
                    setMergeWithSpeciesId(null);
                  }}
                  className="w-full px-3 py-2 text-left text-cyan-600 hover:bg-cyan-50 border-t text-sm font-medium"
                  data-testid="submit-new-species-btn"
                >
                  + Submit as new species (add taxonomy)
                </button>
              </div>
            )}

            {/* Multiple common names ("also known as") */}
            <div className="mt-2 space-y-2" data-testid="common-names-section">
              {commonNames.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {commonNames.map((cn, idx) => (
                    <span
                      key={`${cn}-${idx}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-xs"
                      data-testid={`common-name-chip-${idx}`}
                    >
                      {idx === 0 && <span className="font-semibold">★</span>}
                      {cn}
                      <button
                        type="button"
                        onClick={() => removeCommonName(idx)}
                        className="hover:text-red-600"
                        aria-label={`Remove ${cn}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-1">
                <Input
                  value={newCommonName}
                  onChange={(e) => setNewCommonName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCommonName();
                    }
                  }}
                  placeholder={'Add another name (e.g., "Anemonefish")'}
                  className="text-xs h-8"
                  data-testid="add-common-name-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addCommonName}
                  className="h-8 px-3"
                  data-testid="add-common-name-btn"
                >
                  +
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                The first name is the primary common name. Add alternatives like "Also known as" entries.
              </p>
            </div>

            {/* Merge banner */}
            {mergeWithSpeciesId && (
              <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md p-2">
                ✓ You selected an existing species from suggestions. Admins will see this pre-marked for merge.
              </div>
            )}
          </div>

          {/* Date of Observation */}
          <div>
            <Label htmlFor="observation_date" className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              Date of Observation *
            </Label>
            <Input
              id="observation_date"
              type="date"
              value={formData.observation_date}
              onChange={(e) => setFormData({ ...formData, observation_date: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          {/* Classification Toggle */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-purple-600" />
                <Label className="font-medium text-sm">Add Taxonomic Classification</Label>
              </div>
              <Switch
                checked={useFullTaxonomy}
                onCheckedChange={setUseFullTaxonomy}
                data-testid="taxonomy-toggle"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {useFullTaxonomy 
                ? "Fill in any classification levels you know. New taxonomy will be verified before being added to the database."
                : "Enable to specify scientific classification. Useful for species not yet in the database."
              }
            </p>
          </div>

          {/* Simple Category (when not using full taxonomy) */}
          {!useFullTaxonomy && (
            <div>
              <Label htmlFor="category">Category (optional)</Label>
              <Select
                value={formData.suggested_category}
                onValueChange={(value) => setFormData({ ...formData, suggested_category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category if known" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Not sure? Leave blank and our Marine Biologists will classify it.
              </p>
            </div>
          )}

          {/* Full Taxonomy Fields - Now with reverse lookup */}
          {useFullTaxonomy && (
            <div className="space-y-3 border-l-2 border-purple-200 pl-3">
              <p className="text-xs text-purple-600 font-medium">
                Select from existing taxonomy, or tick the box next to a field to type a custom value.
              </p>

              {/* Kingdom — typically Animalia, but exposed in case it's not */}
              <div>
                <Label className="text-xs">Kingdom</Label>
                <Select
                  value={formData.taxonomy.kingdom || "Animalia"}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    taxonomy: { ...formData.taxonomy, kingdom: value }
                  })}
                >
                  <SelectTrigger className="h-9" data-testid="taxonomy-kingdom-select">
                    <SelectValue placeholder="Select kingdom..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-48">
                    {["Animalia", "Plantae", "Fungi", "Chromista", "Protozoa", "Bacteria", "Archaea"].map(k => (
                      <SelectItem key={k} value={k}>{k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Phylum */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Phylum</Label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 cursor-pointer" data-testid="sub-phylum-toggle">
                      <input
                        type="checkbox"
                        checked={showSubTaxa.phylum}
                        onChange={(e) => setShowSubTaxa({ ...showSubTaxa, phylum: e.target.checked })}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-[10px] text-gray-500">+sub</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer" data-testid="custom-phylum-toggle">
                      <input
                        type="checkbox"
                        checked={customTaxonomy.phylum}
                        onChange={(e) => {
                          setCustomTaxonomy({ ...customTaxonomy, phylum: e.target.checked });
                          if (e.target.checked) setFormData({ ...formData, taxonomy: { ...formData.taxonomy, phylum: "" } });
                        }}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-[10px] text-gray-500">custom</span>
                    </label>
                  </div>
                </div>
                {customTaxonomy.phylum ? (
                  <Input
                    className="h-9"
                    value={formData.taxonomy.phylum}
                    onChange={(e) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, phylum: e.target.value }
                    })}
                    placeholder="e.g., Chordata"
                    data-testid="custom-phylum-input"
                  />
                ) : (
                  <Select
                    value={formData.taxonomy.phylum}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, phylum: value, class_name: "", order: "", family: "" }
                    })}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select phylum..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-48">
                      {getPhyla().map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {showSubTaxa.phylum && (
                  <Input
                    className="h-9 mt-1"
                    value={formData.taxonomy.subphylum || ""}
                    onChange={(e) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, subphylum: e.target.value }
                    })}
                    placeholder="Subphylum (e.g., Vertebrata)"
                    data-testid="taxonomy-subphylum-input"
                  />
                )}
              </div>

              {/* Class */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Class {!customTaxonomy.class_name && formData.taxonomy.phylum && <span className="text-purple-500">(filtered)</span>}</Label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 cursor-pointer" data-testid="sub-class-toggle">
                      <input
                        type="checkbox"
                        checked={showSubTaxa.class_name}
                        onChange={(e) => setShowSubTaxa({ ...showSubTaxa, class_name: e.target.checked })}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-[10px] text-gray-500">+sub</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer" data-testid="custom-class-toggle">
                      <input
                        type="checkbox"
                        checked={customTaxonomy.class_name}
                        onChange={(e) => {
                          setCustomTaxonomy({ ...customTaxonomy, class_name: e.target.checked });
                          if (e.target.checked) setFormData({ ...formData, taxonomy: { ...formData.taxonomy, class_name: "" } });
                        }}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-[10px] text-gray-500">custom</span>
                    </label>
                  </div>
                </div>
                {customTaxonomy.class_name ? (
                  <Input
                    className="h-9"
                    value={formData.taxonomy.class_name}
                    onChange={(e) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, class_name: e.target.value }
                    })}
                    placeholder="e.g., Actinopterygii"
                    data-testid="custom-class-input"
                  />
                ) : (
                  <Select
                    value={formData.taxonomy.class_name}
                    onValueChange={handleClassSelect}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select class..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-48">
                      {getFilteredClasses().map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {showSubTaxa.class_name && (
                  <Input
                    className="h-9 mt-1"
                    value={formData.taxonomy.subclass || ""}
                    onChange={(e) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, subclass: e.target.value }
                    })}
                    placeholder="Subclass"
                    data-testid="taxonomy-subclass-input"
                  />
                )}
              </div>

              {/* Order */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Order {!customTaxonomy.order && formData.taxonomy.class_name && <span className="text-purple-500">(filtered)</span>}</Label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 cursor-pointer" data-testid="sub-order-toggle">
                      <input
                        type="checkbox"
                        checked={showSubTaxa.order}
                        onChange={(e) => setShowSubTaxa({ ...showSubTaxa, order: e.target.checked })}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-[10px] text-gray-500">+sub</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer" data-testid="custom-order-toggle">
                      <input
                        type="checkbox"
                        checked={customTaxonomy.order}
                        onChange={(e) => {
                          setCustomTaxonomy({ ...customTaxonomy, order: e.target.checked });
                          if (e.target.checked) setFormData({ ...formData, taxonomy: { ...formData.taxonomy, order: "" } });
                        }}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-[10px] text-gray-500">custom</span>
                    </label>
                  </div>
                </div>
                {customTaxonomy.order ? (
                  <Input
                    className="h-9"
                    value={formData.taxonomy.order}
                    onChange={(e) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, order: e.target.value }
                    })}
                    placeholder="e.g., Perciformes"
                    data-testid="custom-order-input"
                  />
                ) : (
                  <Select
                    value={formData.taxonomy.order}
                    onValueChange={handleOrderSelect}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select order..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-48">
                      {getFilteredOrders().map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {showSubTaxa.order && (
                  <Input
                    className="h-9 mt-1"
                    value={formData.taxonomy.suborder || ""}
                    onChange={(e) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, suborder: e.target.value }
                    })}
                    placeholder="Suborder"
                    data-testid="taxonomy-suborder-input"
                  />
                )}
              </div>

              {/* Family */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Family {!customTaxonomy.family && (formData.taxonomy.order || formData.taxonomy.class_name) && <span className="text-purple-500">(filtered)</span>}</Label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 cursor-pointer" data-testid="sub-family-toggle">
                      <input
                        type="checkbox"
                        checked={showSubTaxa.family}
                        onChange={(e) => setShowSubTaxa({ ...showSubTaxa, family: e.target.checked })}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-[10px] text-gray-500">+sub</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer" data-testid="custom-family-toggle">
                      <input
                        type="checkbox"
                        checked={customTaxonomy.family}
                        onChange={(e) => {
                          setCustomTaxonomy({ ...customTaxonomy, family: e.target.checked });
                          if (e.target.checked) setFormData({ ...formData, taxonomy: { ...formData.taxonomy, family: "" } });
                        }}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-[10px] text-gray-500">custom</span>
                    </label>
                  </div>
                </div>
                {customTaxonomy.family ? (
                  <Input
                    className="h-9"
                    value={formData.taxonomy.family}
                    onChange={(e) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, family: e.target.value }
                    })}
                    placeholder="e.g., Labridae"
                    data-testid="custom-family-input"
                  />
                ) : (
                  <Select
                    value={formData.taxonomy.family}
                    onValueChange={handleFamilySelect}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select family..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-48">
                      {getFilteredFamilies().map((f) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {showSubTaxa.family && (
                  <Input
                    className="h-9 mt-1"
                    value={formData.taxonomy.subfamily || ""}
                    onChange={(e) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, subfamily: e.target.value }
                    })}
                    placeholder="Subfamily"
                    data-testid="taxonomy-subfamily-input"
                  />
                )}
              </div>

              {/* Genus (dropdown when family known, custom toggle, +sub) + Species (always text) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">Genus</Label>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1 cursor-pointer" data-testid="sub-genus-toggle">
                        <input
                          type="checkbox"
                          checked={showSubTaxa.genus}
                          onChange={(e) => setShowSubTaxa({ ...showSubTaxa, genus: e.target.checked })}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-[10px] text-gray-500">+sub</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer" data-testid="custom-genus-toggle">
                        <input
                          type="checkbox"
                          checked={customTaxonomy.genus}
                          onChange={(e) => {
                            setCustomTaxonomy({ ...customTaxonomy, genus: e.target.checked });
                            if (e.target.checked) setFormData({ ...formData, taxonomy: { ...formData.taxonomy, genus: "" } });
                          }}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-[10px] text-gray-500">custom</span>
                      </label>
                    </div>
                  </div>
                  {(() => {
                    // When family is known and present in taxonomy data, show
                    // a dropdown of genera in that family. Otherwise fall back
                    // to free-text input.
                    const familyGenera = (() => {
                      const { phylum, class_name, order, family } = formData.taxonomy;
                      const tree = taxonomyData?.tree;
                      if (!tree || !family || customTaxonomy.genus) return null;
                      try {
                        const familyData = tree[phylum]?.classes?.[class_name]?.orders?.[order]?.families?.[family];
                        if (familyData?.genera && typeof familyData.genera === "object") {
                          return Object.keys(familyData.genera).sort();
                        }
                      } catch (_e) { /* taxonomy data may not include full genus tree yet */ }
                      return null;
                    })();
                    if (familyGenera && familyGenera.length > 0) {
                      return (
                        <Select
                          value={formData.taxonomy.genus}
                          onValueChange={(value) => setFormData({
                            ...formData,
                            taxonomy: { ...formData.taxonomy, genus: value }
                          })}
                        >
                          <SelectTrigger className="h-9" data-testid="taxonomy-genus-select">
                            <SelectValue placeholder="Select genus..." />
                          </SelectTrigger>
                          <SelectContent className="bg-white max-h-48">
                            {familyGenera.map((g) => (
                              <SelectItem key={g} value={g}>{g}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }
                    return (
                      <Input
                        className="h-9"
                        value={formData.taxonomy.genus}
                        onChange={(e) => setFormData({
                          ...formData,
                          taxonomy: { ...formData.taxonomy, genus: e.target.value }
                        })}
                        placeholder="e.g., Rhincodon"
                        data-testid="taxonomy-genus-input"
                      />
                    );
                  })()}
                  {showSubTaxa.genus && (
                    <Input
                      className="h-9 mt-1"
                      value={formData.taxonomy.subgenus || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        taxonomy: { ...formData.taxonomy, subgenus: e.target.value }
                      })}
                      placeholder="Subgenus"
                      data-testid="taxonomy-subgenus-input"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs">Species (specific epithet)</Label>
                  <Input
                    className="h-9"
                    value={formData.taxonomy.species}
                    onChange={(e) => setFormData({
                      ...formData,
                      taxonomy: { ...formData.taxonomy, species: e.target.value }
                    })}
                    placeholder="e.g., typus"
                    data-testid="taxonomy-species-input"
                  />
                </div>
              </div>

              {/* Scientific name preview — auto-derived from Genus + species */}
              {derivedScientificName && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-2 text-xs" data-testid="scientific-name-preview">
                  <span className="text-purple-700 font-medium">Scientific name (auto):</span>{" "}
                  <span className="italic text-purple-900">{derivedScientificName}</span>
                </div>
              )}

              {/* Clear taxonomy button */}
              {(formData.taxonomy.phylum || formData.taxonomy.class_name || formData.taxonomy.order || formData.taxonomy.family || formData.taxonomy.genus || formData.taxonomy.species) && (
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    taxonomy: { kingdom: "Animalia", phylum: "", subphylum: "", class_name: "", subclass: "", order: "", suborder: "", family: "", subfamily: "", genus: "", subgenus: "", species: "" }
                  })}
                  className="text-xs text-gray-500 hover:text-red-500 underline"
                >
                  Clear taxonomy selection
                </button>
              )}
            </div>
          )}

          {/* Location Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-600" />
              Location *
            </Label>
            
            {/* Region Dropdown */}
            <div>
              <Label className="text-xs">Region *</Label>
              <Select
                value={formData.region}
                onValueChange={handleRegionSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-48">
                  {locationData?.all_regions && Object.keys(locationData.all_regions).map((region) => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Country Dropdown */}
            {formData.region && (
              <div>
                <Label className="text-xs">Country *</Label>
                <Select
                  value={formData.country}
                  onValueChange={handleCountrySelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-48">
                    {getCountriesForRegion().map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Area Dropdown - always shown after country is selected */}
            {formData.country && (
              <div>
                <Label className="text-xs">
                  Area
                  {getAreasForCountry().length > 0 && (
                    <span className="text-gray-400 ml-1">
                      ({getAreasForCountry().length} areas)
                    </span>
                  )}
                </Label>
                {getAreasForCountry().length > 0 ? (
                  <Select
                    value={formData.location_id || "SKIP"}
                    onValueChange={handleAreaSelect}
                  >
                    <SelectTrigger data-testid="area-select">
                      <SelectValue placeholder="Select area (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-48">
                      <SelectItem value="SKIP">All areas</SelectItem>
                      {getAreasForCountry().map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-200">
                    No areas registered for {formData.country}
                  </div>
                )}
              </div>
            )}

            {/* Dive Site Dropdown - filtered by area/country */}
            {formData.country && (
              <div>
                <Label className="text-xs">
                  Dive Site (optional)
                  {filteredDiveSites.length > 0 && (
                    <span className="text-gray-400 ml-1">
                      ({filteredDiveSites.length} sites{formData.location_name ? ` in ${formData.location_name}` : ` in ${formData.country}`})
                    </span>
                  )}
                </Label>
                <Select
                  value={formData.dive_site_id || (formData.dive_site_name === "N/A" ? "N/A" : "")}
                  onValueChange={handleDiveSiteSelect}
                >
                  <SelectTrigger data-testid="dive-site-select">
                    <SelectValue placeholder={
                      filteredDiveSites.length > 0 
                        ? "Select dive site" 
                        : "No dive sites registered for this country"
                    } />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-48">
                    <SelectItem value="N/A" data-testid="dive-site-na-option">
                      N/A - Unknown / Not applicable
                    </SelectItem>
                    {filteredDiveSites.length > 0 ? (
                      filteredDiveSites.map((site) => (
                        <SelectItem key={site.id} value={site.id}>
                          {site.name} {site.location ? `(${site.location})` : ""}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No dive sites registered for {formData.country}
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Selected Location Summary */}
            {(formData.region || formData.country || formData.dive_site_name) && (
              <div className="bg-emerald-50 rounded-lg p-2 text-sm">
                <div className="flex items-center gap-2 text-emerald-700">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {[formData.region, formData.country, formData.location_name, formData.dive_site_name].filter(Boolean).join(" → ")}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Photo Upload - MANDATORY */}
          <div>
            <Label className="flex items-center gap-2">
              Photos *
              <span className="text-xs text-red-500 font-normal">(Required)</span>
            </Label>
            <MultiImageUpload
              images={formData.image_urls}
              onImagesChange={(urls) => setFormData({ ...formData, image_urls: urls })}
              uploadEndpoint="/api/upload/marine-species-image"
              maxImages={5}
              label=""
            />
            {formData.image_urls.length === 0 && (
              <div className="flex items-center gap-2 text-amber-600 text-xs mt-1">
                <AlertCircle className="w-3 h-3" />
                <span>At least one clear photo is required for verification</span>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h4 className="font-medium text-blue-800 mb-1">What happens next?</h4>
            <p className="text-sm text-blue-700">
              Your submission will be reviewed by our Marine Biologists. If approved, 
              it will be added to our database and you&apos;ll be credited as the first to spot it!
            </p>
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <Button
              type="submit"
              disabled={loading || formData.image_urls.length === 0}
              className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                "Submit for Review"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpeciesSubmissionModal;
